import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useUpdateCalendarBlockMutationOptions } from "../calendar/mutations";
import useFindUpdateInboxQueryKey from "../../hooks/inbox/useFindUpdateInboxQueryKey";
import { BlockType } from "../../types/block/type";
import { calendarQueryKeys } from "../calendar/queries";
import { useSetRecoilState } from "recoil";
import { toastState } from "../../recoil/toast/toastState";
import { isAxiosError } from "axios";

import { ToastKind } from "../../types/toast";
import { getTargetFromBlockType, getToastMessageByKind } from "../../utils/toast/toastUtils";
import { debounceInvalidate } from "../../services/optimistic-updates/debounceInvalidate";
import { inboxQueryKeys } from "../inbox/queries";

export const useDeleteBlockMutation = () => {
  const queryClient = useQueryClient();
  const { findUpdateInboxQueryKey } = useFindUpdateInboxQueryKey();
  const setToast = useSetRecoilState(toastState);

  return useMutation({
    ...useUpdateCalendarBlockMutationOptions(),
    onMutate: async ({ id, payload, prevData }) => {
      const updateInboxQueryKey = findUpdateInboxQueryKey(prevData);

      if (updateInboxQueryKey) {
        await queryClient.cancelQueries({ queryKey: updateInboxQueryKey });
        const previousData = queryClient.getQueryData<BlockType[]>(updateInboxQueryKey);
        if (previousData) {
          queryClient.setQueryData(
            updateInboxQueryKey,
            previousData.filter((block) => block.id !== prevData.id)
          );
        }
        return { previousData, updateInboxQueryKey };
      }
    },
    onSuccess: (data, variables, context) => {
      const { prevData } = variables;
      setToast({
        type: ToastKind.DELETE,
        isVisible: true,
        message: getToastMessageByKind({
          type: ToastKind.DELETE,
          target: getTargetFromBlockType(prevData.blockType),
        }),
        triggerId: Date.now(),
      });

      // 반복인 경우에만 즉시 invalidate 처리(inbox 전체, calendar 전체)
      const isRecurringBlock =
        (prevData.recurrence && prevData.recurrence?.length > 0) || Boolean(prevData.originalId);

      if (isRecurringBlock) {
        debounceInvalidate.enqueueInvalidate(inboxQueryKeys.all(), {
          shouldInvalidateImmediately: true,
        });
        debounceInvalidate.enqueueInvalidate(calendarQueryKeys.all, {
          shouldInvalidateImmediately: true,
        });
      } else {
        // 여기는 일반블록 delete이기 때문에 inbox에서 원래 있던 영역만 Invalidate 해주면 됨
        if (context?.updateInboxQueryKey) {
          debounceInvalidate.enqueueInvalidate(context.updateInboxQueryKey);
        }
        debounceInvalidate.enqueueInvalidate(calendarQueryKeys.all);
      }
    },
    onError: (err, variables, context) => {
      // 기획) Network Error일 때에만 아래 로직 동작하도록 수정
      if (isAxiosError(err)) {
        debounceInvalidate.enqueueInvalidate(calendarQueryKeys.all, {
          shouldInvalidateImmediately: true,
        });
        // 에러 발생 시 이전 데이터로 롤백
        if (context?.previousData && context?.updateInboxQueryKey) {
          queryClient.setQueryData(context.updateInboxQueryKey, context.previousData);
        }

        setToast({
          type: ToastKind.ERROR,
          isVisible: true,
          message: getToastMessageByKind({
            type: ToastKind.ERROR,
            action: "delete",
          }),
          triggerId: Date.now(),
        });
      }
    },
  });
};
